/* eslint-disable jira/integration/test-id-by-folder-structure */
import React, { useCallback, useState } from 'react';
import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import AddIcon from '@atlaskit/icon/core/migration/add';
import { useIntl } from '@atlassian/jira-intl';
import { useLoomTouchpointVariant } from '@atlassian/jira-loom-utils/src/controllers/use-loom-touchpoint-variant/index.tsx';
import { LoomTouchpointVariant } from '@atlassian/jira-loom-utils/src/controllers/use-loom-touchpoint-variant/types.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import InsertExistingLoomItem from './insert-existing-loom-item/index.tsx';
import { messages } from './messages.tsx';
import { RecordNewLoomItem } from './record-new-loom-item/index.tsx';
import type { Props } from './types.tsx';

const LoomActionsMenu = ({ onShowInsertLoomLinkForm, onInsertLink }: Props) => {
	const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { loomTouchpointVariant } = useLoomTouchpointVariant();
	const handleOpenChange = useCallback(
		({ isOpen }: { isOpen: boolean }) => {
			setIsDropdownMenuOpen(isOpen);
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'toggled',
					actionSubject: 'dropdownMenu',
				}),
				'add-loom-icon-dropdown-menu',
				{ isOpen },
			);
		},
		[createAnalyticsEvent],
	);

	// under cross-flow, we don't allow users to even add existing Looms
	if (
		loomTouchpointVariant === LoomTouchpointVariant.CROSSFLOW ||
		loomTouchpointVariant === LoomTouchpointVariant.NONE
	) {
		return null;
	}

	return (
		<DropdownMenu
			testId="issue-view-base.content.loom-videos.ui.co-use.loom-actions-menu.drop-down-menu"
			defaultOpen={isDropdownMenuOpen}
			onOpenChange={handleOpenChange}
			placement="bottom-end"
			trigger={({ triggerRef, ...props }) => (
				<Button
					{...props}
					iconBefore={
						<AddIcon
							LEGACY_size="small"
							color="currentColor"
							{...props}
							label={formatMessage(messages.label)}
						/>
					}
					ref={triggerRef}
					appearance="subtle"
					spacing="compact"
				/>
			)}
			shouldRenderToParent // This is needed to render the dropdown menu in the same container as the trigger instead of in React.Portal (otherwise domRef.current?.closest('[data-loom-insert-target-marker]' is undefined )
		>
			<DropdownItemGroup>
				<RecordNewLoomItem onInsertLink={onInsertLink} />
				<InsertExistingLoomItem onShowInsertLoomLinkForm={onShowInsertLoomLinkForm} />
			</DropdownItemGroup>
		</DropdownMenu>
	);
};

export default LoomActionsMenu;
